import { Box, Typography, Drawer, Chip, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import AppBar from "@mui/material/AppBar";
import MenuIcon from "@mui/icons-material/Menu";
import IconButton from "@mui/material/IconButton";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { config } from "@config";
import { useUserInfoContext } from "../../lib/contexts/UserInfoContext";
import { useChatSessionsContext } from "../../lib/contexts/ChatSessionsContext";
import ContentPanel from "../ContentPanel/ContentPanel";
import { Profile, profileContext } from "../../pages/settings/Profile";
import { useState } from "react";
import { getEnvLabel } from "./envLabel";
import { APP_VERSION } from "../../lib/constants";
import { useSystemErrorAlert } from "../../lib/hooks/useSystemErrorAlert";

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isLearningSite = Boolean(config.learningPersonaId);
  const isLearningWorkspace = pathname.startsWith('/learn/');
  const isAdminPath = pathname === '/admin' || pathname.startsWith('/admin/');
  const isSidebarRoute = isLearningWorkspace || (isAdminPath && isLearningSite);
  const { userInfo } = useUserInfoContext();
  const {
    handleDrawerToggle,
    mobileOpen,
  } = useChatSessionsContext();
  const envLabel = getEnvLabel(window.location.hostname, config);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [activateProfile, setActivateProfile] = useState(false);
  const { hasNewErrors } = useSystemErrorAlert(userInfo);
  const isErrorsTab = pathname.startsWith("/admin/errors");

  return (
    <profileContext.Provider value={{ activateProfile, setActivateProfile }}>
    <>
      <Profile />
      <Drawer anchor="left" open={mobileOpen} onClose={handleDrawerToggle}>
        <ContentPanel onItemClick={handleDrawerToggle} />
      </Drawer>
      <Box sx={{ position: "sticky", top: 0, zIndex: 1100 }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            height: "48px",
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              px: { xs: 1.5, md: 2.5 },
            }}
          >
            {/* Left: Menu + Logo */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
              {userInfo != null && !isSidebarRoute && (
                <IconButton
                  aria-label="open drawer"
                  edge="start"
                  size="small"
                  onClick={handleDrawerToggle}
                  sx={{ color: "text.primary" }}
                >
                  <MenuIcon fontSize="small" />
                </IconButton>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography
                  component={isSidebarRoute ? "span" : "a"}
                  {...(!isSidebarRoute ? { href: "/" } : {})}
                  sx={{
                    fontFamily: "'Newsreader', Georgia, serif",
                    fontWeight: 500,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    color: "text.primary",
                    letterSpacing: "-0.02em",
                    textDecoration: "none",
                    lineHeight: 1,
                    display: "inline-flex",
                    alignItems: "center",
                  }}
                  variant="h6"
                >
                  {config.styledText}
                </Typography>
                {envLabel && (
                  <Box
                    sx={{
                      backgroundColor: "error.main",
                      color: "white",
                      px: 1,
                      py: 0.2,
                      borderRadius: 1,
                      fontSize: "0.75rem",
                      fontWeight: "bold",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {envLabel}
                  </Box>
                )}
              </Box>
            </Box>
            {/* Right: Error Alert + Version */}
            <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1.5 }}>
              {hasNewErrors && !isErrorsTab && (
                <Tooltip title="Click to view new unresolved system errors">
                  <Chip
                    icon={<ErrorOutlineIcon sx={{ color: "white !important", fontSize: "1rem" }} />}
                    label={isMobile ? "Errors" : "New System Errors"}
                    color="error"
                    size="small"
                    clickable
                    onClick={() => navigate("/admin/errors")}
                    sx={{
                      fontWeight: "bold",
                      fontSize: "0.75rem",
                      cursor: "pointer",
                      animation: "pulse 2s infinite",
                      "@keyframes pulse": {
                        "0%": { opacity: 1 },
                        "50%": { opacity: 0.7 },
                        "100%": { opacity: 1 },
                      },
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: "none",
                      },
                    }}
                  />
                </Tooltip>
              )}
              {APP_VERSION && (
                <Typography
                  variant="caption"
                  sx={{
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    opacity: 0.75,
                    fontFamily: "monospace",
                    letterSpacing: "0.02em",
                    userSelect: "all",
                  }}
                  title={`Site Version: ${APP_VERSION}`}
                >
                  v{APP_VERSION}
                </Typography>
              )}
            </Box>
          </Box>
        </AppBar>
      </Box>
      <Outlet />
    </>
    </profileContext.Provider>
  );
}
